import React, { Dispatch } from 'react';
import { Stack } from '@mui/material';
import { Autocomplete, TextField } from '@mui/material';
import { sharedClasses } from '../../../../Components/CustomUIElements/sharedClasses';
import { styles } from '../../styles';
import { FormTextField } from '../../../../Components/CustomUIElements/FormTextField';
import { SingleDatePicker } from '../../../../Components/CustomUIElements/SingleDatePicker';
import dayjs from 'dayjs';
import { Checkbox, FormControlLabel } from '@mui/material';
import DocTypeIcon from '../../../../Components/Utilities/DocTypeIcon';
import { IntelliHRState, IntelliHRAction } from './intelliHRReducer';
import { IIntelliHrData } from '../../../types';
import { useQueryClient } from '@tanstack/react-query';

interface IntelliHRFirstStepProps {
  state: IntelliHRState;
  dispatch: Dispatch<IntelliHRAction>;
  fieldRefs: {
    firstName: React.RefObject<HTMLInputElement>;
    lastName: React.RefObject<HTMLInputElement>;
    email: React.RefObject<HTMLInputElement>;
    phone: React.RefObject<HTMLInputElement>;
  };
}

export default function IntelliHRFirstStep({
  state,
  dispatch,
  fieldRefs
}: IntelliHRFirstStepProps) {
  const queryClient = useQueryClient();
  const intelliHrData = queryClient.getQueryData<IIntelliHrData>(['intelliHr data']);
  const queryState = queryClient.getQueryState(['intelliHr data']);
  const loadingIntelliHrData = queryState?.fetchStatus === 'fetching';

  if (!state.candidate) return null;

  return (
    <>
      <Stack sx={styles.cognologySectionTitle}>Create Person</Stack>
      <Autocomplete
        disablePortal
        options={intelliHrData?.titles || []}
        value={state.title}
        loading={loadingIntelliHrData}
        sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
        slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Title"
            placeholder="Please select"
            slotProps={{ inputLabel: { shrink: true } }}
          />
        )}
        onChange={(event, value) => dispatch({ type: 'SET_TITLE', payload: value })}
      />
      <Stack sx={styles.modalDoubleColumn}>
        <FormTextField
          label="First name"
          value={state.candidate.firstname || ''}
          required
          fullWidth
          onChange={(e) =>
            dispatch({
              type: 'SET_CANDIDATE',
              payload: state.candidate ? { ...state.candidate, firstname: e.target.value } : null
            })
          }
          error={state.errors.firstName}
          innerRef={fieldRefs.firstName}
        />
        <FormTextField
          label="Last name"
          value={state.candidate.lastname || ''}
          required
          fullWidth
          onChange={(e) =>
            dispatch({
              type: 'SET_CANDIDATE',
              payload: state.candidate ? { ...state.candidate, lastname: e.target.value } : null
            })
          }
          error={state.errors.lastName}
          innerRef={fieldRefs.lastName}
        />
      </Stack>
      <FormTextField
        label="Preferred name"
        value={state.preferredName}
        onChange={(e) => dispatch({ type: 'SET_PREFERRED_NAME', payload: e.target.value })}
        styles={{ width: '48%' }}
      />
      <Stack sx={styles.modalDoubleColumn}>
        <Autocomplete
          disablePortal
          options={intelliHrData?.genders || []}
          getOptionLabel={(option) => option[0]}
          value={state.gender}
          loading={loadingIntelliHrData}
          sx={{ ...sharedClasses.formAutocomplete, width: '48%', marginTop: 3 }}
          slotProps={{ listbox: { style: sharedClasses.autoCompleteListStyles } }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Gender"
              placeholder="Please select"
              slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
          onChange={(event, value) => dispatch({ type: 'SET_GENDER', payload: value })}
        />
        <Stack sx={{ width: '48%' }}>
          <SingleDatePicker
            value={state.dateOfBirth}
            label="Date of birth"
            inputStyles={{
              ...sharedClasses.datePicker,
              width: '100%',
              height: '34px',
              marginTop: '2px'
            }}
            onChange={(value) => {
              if (!value?.isValid()) {
                dispatch({ type: 'SET_DATE_ERROR', payload: 'Please input a valid date' });
              } else {
                dispatch({ type: 'SET_DATE_ERROR', payload: '' });
                dispatch({
                  type: 'SET_DATE_OF_BIRTH',
                  payload: dayjs(value.toDate()).toDate()
                });
              }
            }}
            error={state.dateError || state.errors.dateOfBirth}
          />
        </Stack>
      </Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <Stack sx={{ width: '48%' }}>
          <FormTextField
            label="Email"
            value={state.candidate.email}
            required
            fullWidth
            onChange={(e) =>
              dispatch({
                type: 'SET_CANDIDATE',
                payload: state.candidate ? { ...state.candidate, email: e.target.value } : null
              })
            }
            error={state.errors.email}
            innerRef={fieldRefs.email}
          />
          <FormControlLabel
            control={
              <Checkbox
                sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
                checked={state.personalEmail}
                onChange={(event) =>
                  dispatch({ type: 'SET_PERSONAL_EMAIL', payload: event.target.checked })
                }
              />
            }
            label="Email is personal"
            sx={{ ...sharedClasses.checkboxLabel, margin: 'unset', marginTop: '4px' }}
          />
        </Stack>
        <Stack sx={{ width: '48%' }}>
          <FormTextField
            label="Phone"
            value={state.candidate.phone1}
            onChange={(e) =>
              dispatch({
                type: 'SET_CANDIDATE',
                payload: state.candidate ? { ...state.candidate, phone1: e.target.value } : null
              })
            }
            required
            fullWidth
            error={state.errors.phone}
            innerRef={fieldRefs.phone}
          />
          <FormControlLabel
            control={
              <Checkbox
                sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
                checked={state.personalPhone}
                onChange={(event) =>
                  dispatch({ type: 'SET_PERSONAL_PHONE', payload: event.target.checked })
                }
              />
            }
            label="Phone is personal"
            sx={{ ...sharedClasses.checkboxLabel, margin: 'unset', marginTop: '4px' }}
          />
        </Stack>
      </Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <FormTextField
          label="Emergency contact name"
          value={state.emergencyContact.name}
          fullWidth
          onChange={(e) =>
            dispatch({
              type: 'SET_EMERGENCY_CONTACT',
              payload: { name: e.target.value }
            })
          }
        />
        <FormTextField
          label="Emergency contact relationship"
          value={state.emergencyContact.relationship}
          fullWidth
          onChange={(e) =>
            dispatch({
              type: 'SET_EMERGENCY_CONTACT',
              payload: { relationship: e.target.value }
            })
          }
        />
      </Stack>
      <Stack sx={styles.modalDoubleColumn}>
        <FormTextField
          label="Emergency contact phone"
          value={state.emergencyContact.phone}
          fullWidth
          onChange={(e) =>
            dispatch({
              type: 'SET_EMERGENCY_CONTACT',
              payload: { phone: e.target.value }
            })
          }
        />
        <FormTextField
          label="Emergency contact email"
          value={state.emergencyContact.email}
          fullWidth
          onChange={(e) =>
            dispatch({
              type: 'SET_EMERGENCY_CONTACT',
              payload: { email: e.target.value }
            })
          }
        />
      </Stack>
      <Stack sx={styles.cognologySectionTitle}>Attachments</Stack>
      {state.attachments.map((attachment, index) => (
        <Stack key={index} sx={styles.cognologyAttachmentContainer}>
          <Checkbox
            sx={{ ...sharedClasses.formCheckbox, padding: 'unset', marginRight: 1 }}
            checked={attachment.checked}
            onChange={(event) =>
              dispatch({
                type: 'SET_ATTACHMENTS',
                payload: state.attachments.map((attachment, i) =>
                  i === index ? { ...attachment, checked: event.target.checked } : attachment
                )
              })
            }
          />
          <Stack sx={{ ...styles.fileIcon, minWidth: '50px', cursor: 'unset' }}>
            {DocTypeIcon(attachment.type.split('/').pop())}
          </Stack>
          <FormTextField
            label="File name"
            value={attachment.file_name}
            onChange={(e) =>
              dispatch({
                type: 'SET_ATTACHMENTS',
                payload: state.attachments.map((attachment, i) =>
                  i === index ? { ...attachment, file_name: e.target.value } : attachment
                )
              })
            }
            fullWidth
          />
        </Stack>
      ))}
    </>
  );
}
